/**
 * Small helpers shared by every module. The layout renders the theme settings modules need as JSON in
 * script[data-theme-config], read through themeConfig().
 */
export const qs = (selector, root = document) => root.querySelector(selector);
export const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

/**
 * Runs init(el) once for every element matching selector, now and in sections the theme editor adds or
 * re-renders (shopify:section:load). Returns a function that binds new elements inside a scope, for HTML
 * swapped in by fetch.
 */
export function onSection(selector, init) {
  const done = new WeakSet();
  const run = (scope = document) => {
    const found = qsa(selector, scope);
    if (scope.matches?.(selector)) found.unshift(scope);
    found.forEach((el) => {
      if (done.has(el)) return;
      done.add(el);
      init(el);
    });
  };
  run();
  document.addEventListener('shopify:section:load', (e) => run(e.target));
  return run;
}

let config;
export const themeConfig = () => {
  if (!config) {
    try {
      config = JSON.parse(qs('script[data-theme-config]')?.textContent || '{}');
    } catch (_) {
      config = {};
    }
  }
  return config;
};

export const cartAddUrl = () => themeConfig().routes?.cartAdd || '/cart/add.js';

/** Matches the desktop layout (min-width: 1080px) */
export const desktopMq = window.matchMedia('(min-width: 1080px)');

/** The current time in Europe/Amsterdam as a local Date, for opening hours and store events */
export const amsterdamNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));

/** True when the visitor turned animations off in the accessibility panel */
export const reduceMotion = () => document.documentElement.classList.contains('dmi-a11y-reduce-motion');

/** Cents to a label: 1295 → "€12,95", 1200 → "€12,-" */
export const money = (cents) => {
  const amount = Math.round(Math.abs(Number(cents) || 0));
  const whole = Math.floor(amount / 100);
  const frac = amount % 100;
  return frac === 0 ? `€${whole},-` : `€${whole},${String(frac).padStart(2, '0')}`;
};

/** "44.95", "44,95", 4495, { amount }, { value } → cents (NaN when empty). Whole numbers are taken as cents already. */
export const toCents = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'object') return toCents(value.amount ?? value.value ?? value.shopMoney ?? value.presentmentMoney);
  const raw = String(value).trim().replace(',', '.');
  const n = Number(raw);
  if (!raw || !Number.isFinite(n)) return NaN;
  return raw.includes('.') ? Math.round(n * 100) : n;
};

export const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** HTML that arrived through fetch or the theme editor never ran its module scripts: run them now. */
export function runModuleScripts(root) {
  qsa('script[type="module"]', root).forEach((old) => {
    const script = document.createElement('script');
    script.type = 'module';
    if (old.src) script.src = old.src;
    else script.textContent = old.textContent;
    old.replaceWith(script);
  });
}

/** The section element from a Section Rendering API response */
export const parseSection = (html, sectionId) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.getElementById(`shopify-section-${sectionId}`) || doc.body.firstElementChild;
};

/** Renders one section for url through the Section Rendering API; throws when the request fails */
export async function fetchSection(url, sectionId, init) {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('section_id', sectionId);
  const res = await fetch(target, init);
  if (!res.ok) throw new Error(`Section request failed: ${res.status}`);
  const section = parseSection(await res.text(), sectionId);
  if (!section) throw new Error('Section missing');
  return section;
}

/** sessionStorage cache with a time to live in ms; both calls are safe in private mode */
export function cacheGet(key, ttl) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key));
    return parsed && Date.now() - parsed.t <= ttl ? parsed.v : null;
  } catch (_) {
    return null;
  }
}

export function cacheSet(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }));
  } catch (_) { }
}

export async function fetchJson(url, init = { credentials: 'omit' }) {
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json();
}
